// AthkarCounter — post-prayer dhikr counter for Yawmi HabitTab.
// Scope: Yawmi (habit-tracking UI). Content: static JSON from Hisn al-Muslim
// at public/data/adhkar.v1.json — no AI-generated text, no live Hisni fetch.
// State stored per-day in localStorage: aweni_yawmi_athkar_{date}_{dhikrId}.
import type { AweniContext } from '../../types';

interface AdhkarSource {
  book: string;
  chapter: string;
  hadith_number: string;
}

interface AdhkarItem {
  id: string;
  arabic: string;
  translit: string;
  label: Record<string, string>;
  target: number;
  sources: AdhkarSource[];
  sunnah_com_url?: string;
  hisni_path?: string;
}

interface AdhkarData {
  _source: string;
  items: AdhkarItem[];
}

const STORAGE_PREFIX = 'aweni_yawmi_athkar';
const MILESTONE_COUNTS = new Set([33, 66, 99, 100]);

export class AthkarCounter {
  private el: HTMLElement;
  private items: AdhkarItem[] = [];
  private sourceRef = '';
  private onComplete: (() => void) | null;
  private isMounted = false;

  constructor(container: HTMLElement, onComplete?: () => void) {
    this.el = container;
    this.onComplete = onComplete ?? null;
  }

  async mount(ctx: AweniContext): Promise<void> {
    this.isMounted = true;
    await this.loadDataAndRender(ctx);
  }

  private async loadDataAndRender(ctx: AweniContext): Promise<void> {
    this.renderLoading(ctx);
    try {
      const url = '/data/adhkar.v1.json';
      const res = await fetch(url, { cache: 'default' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data: AdhkarData = await res.json();
      this.items = data.items ?? [];
      this.sourceRef = data._source ?? '';
    } catch (err) {
      console.warn('[AthkarCounter] Failed to load adhkar data', err);
      this.items = [];
      this.sourceRef = '';
    }
    if (!this.isMounted) return;
    this.render(ctx);
  }

  unmount(): void {
    this.isMounted = false;
    this.el.innerHTML = '';
  }

  private today(): string {
    return new Date().toISOString().slice(0, 10);
  }

  private storageKey(id: string): string {
    return `${STORAGE_PREFIX}_${this.today()}_${id}`;
  }

  private getCount(id: string): number {
    try {
      return parseInt(localStorage.getItem(this.storageKey(id)) || '0', 10);
    } catch {
      return 0;
    }
  }

  private setCount(id: string, count: number): void {
    try {
      localStorage.setItem(this.storageKey(id), String(count));
    } catch { /* storage full — ignore */ }
  }

  private isAllDone(): boolean {
    return this.items.length > 0 && this.items.every(item => this.getCount(item.id) >= item.target);
  }

  private renderLoading(ctx: AweniContext): void {
    const t = (k: string, fb: string) => ctx.i18n.t(k) || fb;
    this.el.innerHTML = `<div style="text-align:center;padding:40px 0;color:var(--ln-text-secondary)">${t('loading', '…')}</div>`;
  }

  private openInfoSheet(item: AdhkarItem, ctx: AweniContext): void {
    const t = (k: string, fb: string) => ctx.i18n.t(k) || fb;
    const lang = ctx.i18n.locale;
    const label = item.label[lang] || item.label['en'] || item.translit;

    const sourcesHtml = item.sources.map(s => `
      <div style="margin-bottom:10px;padding:10px;background:var(--ln-bg-card);border-radius:var(--ln-radius-md)">
        <div style="font-weight:600;font-size:.85rem">${s.book}</div>
        <div style="font-size:.78rem;color:var(--ln-text-secondary);margin-top:2px">${s.chapter}</div>
        <div style="font-size:.78rem;color:var(--ln-text-secondary)">
          ${t('yawmi_athkar_hadith_no', 'Hadith no.')} ${s.hadith_number}
        </div>
      </div>`).join('');

    const hisniHref = item.hisni_path ? `#/hisni${item.hisni_path}` : '#/hisni/adhkar/post-prayer';
    const sunnahHref = item.sunnah_com_url ?? '';

    const overlay = document.createElement('div');
    overlay.id = 'athkar-info-overlay';
    overlay.style.cssText = [
      'position:fixed;inset:0;z-index:9999',
      'background:rgba(0,0,0,.55)',
      'display:flex;flex-direction:column;justify-content:flex-end',
    ].join(';');

    overlay.innerHTML = `
      <div role="dialog" aria-modal="true" style="
          background:var(--ln-bg-primary);
          border-radius:var(--ln-radius-lg) var(--ln-radius-lg) 0 0;
          padding:20px 16px 32px;
          max-height:82vh;overflow-y:auto">
        <div style="width:36px;height:4px;background:var(--ln-border);border-radius:2px;margin:0 auto 18px"></div>
        <div style="font-family:var(--ln-font-arabic,'serif');font-size:1.75rem;
            text-align:center;margin-bottom:6px;direction:rtl;
            color:var(--ln-text-primary)">${item.arabic}</div>
        <div style="text-align:center;font-size:.88rem;
            color:var(--ln-text-secondary);margin-bottom:2px">${item.translit}</div>
        <div style="text-align:center;font-size:.85rem;
            margin-bottom:18px">${label}</div>
        <div style="font-size:.78rem;font-weight:600;
            color:var(--ln-text-secondary);margin-bottom:8px;
            text-transform:uppercase;letter-spacing:.04em">
          ${t('yawmi_athkar_sources_label', 'Sources')}
        </div>
        ${sourcesHtml}
        <div style="font-size:.72rem;color:var(--ln-text-secondary);
            font-style:italic;margin-bottom:18px">${this.sourceRef}</div>
        <a href="${hisniHref}"
            style="display:block;text-align:center;padding:13px;
              background:var(--ln-accent);color:var(--ln-accent-text);
              border-radius:var(--ln-radius-md);font-weight:700;
              text-decoration:none;font-size:.9rem;margin-bottom:8px">
          ${t('yawmi_athkar_open_hisni', 'View full reference in Hisni →')}
        </a>
        ${sunnahHref ? `
        <a href="${sunnahHref}" target="_blank" rel="noopener noreferrer"
            style="display:block;text-align:center;padding:10px;
              background:transparent;border:1px solid var(--ln-border);
              border-radius:var(--ln-radius-md);font-size:.85rem;
              color:var(--ln-text-secondary);text-decoration:none;margin-bottom:8px">
          ${t('yawmi_athkar_view_sunnah', 'View on sunnah.com ↗')}
        </a>` : ''}
        <button id="athkar-sheet-close"
            style="width:100%;padding:11px;background:transparent;
              border:1px solid var(--ln-border);border-radius:var(--ln-radius-md);
              cursor:pointer;font-size:.9rem;color:var(--ln-text-secondary)">
          ${t('yawmi_athkar_close', 'Close')}
        </button>
      </div>`;

    document.body.appendChild(overlay);
    overlay.querySelector('#athkar-sheet-close')?.addEventListener('click', () => overlay.remove());
    overlay.addEventListener('click', e => { if (e.target === overlay) overlay.remove(); });
  }

  private render(ctx: AweniContext): void {
    const t = (k: string, fb: string) => ctx.i18n.t(k) || fb;
    const lang = ctx.i18n.locale;

    if (this.items.length === 0) {
      this.el.innerHTML = `
        <div aria-live="polite" style="
            text-align:center;
            color:var(--ln-text-secondary);
            padding-block:24px;
            padding-inline:12px;
            margin-inline:auto;
            max-width:420px">
          <p style="margin:0 0 12px">
            ${t('athkar_load_failed', 'Could not load adhkar. Please check your connection.')}
          </p>
          <button type="button" class="athkar-retry" style="
              min-height:44px;
              min-width:44px;
              padding-block:10px;
              padding-inline:16px;
              border-radius:var(--ln-radius-md);
              border:1px solid var(--ln-border);
              background:var(--ln-bg-card);
              color:var(--ln-text-primary);
              font-size:.9rem;
              font-weight:600;
              cursor:pointer">
            ${t('common_retry', 'Retry')}
          </button>
        </div>`;
      this.el.querySelector<HTMLButtonElement>('.athkar-retry')?.addEventListener('click', () => {
        void this.loadDataAndRender(ctx);
      }, { once: true });
      return;
    }

    const allDone = this.isAllDone();

    const cardsHtml = this.items.map(item => {
      const count = this.getCount(item.id);
      const done = count >= item.target;
      const pct = Math.min(100, Math.round((count / item.target) * 100));
      const label = item.label[lang] || item.label['en'] || item.translit;

      return `
        <div style="background:var(--ln-bg-card);border-radius:var(--ln-radius-lg);
            padding:16px;margin-bottom:12px;transition:opacity var(--motion-duration-base,200ms);
            ${done ? 'opacity:.6' : ''}">
          <div style="display:flex;justify-content:space-between;
              align-items:flex-start;margin-bottom:12px">
            <div style="flex:1;min-width:0">
              <div style="font-family:var(--ln-font-arabic,'serif');
                  font-size:1.35rem;direction:rtl;margin-bottom:2px;
                  color:var(--ln-text-primary)">${item.arabic}</div>
              <div style="font-size:.78rem;color:var(--ln-text-secondary)">${item.translit}</div>
              <div style="font-size:.78rem;color:var(--ln-text-secondary)">${label}</div>
            </div>
            <button class="athkar-info" data-id="${item.id}"
                aria-label="${t('yawmi_athkar_info_aria', 'Source information')}"
                style="width:32px;height:32px;border-radius:50%;
                  border:1px solid var(--ln-border);background:transparent;
                  cursor:pointer;font-size:.85rem;
                  color:var(--ln-text-secondary);flex-shrink:0;margin-left:8px">ℹ</button>
          </div>
          <div style="display:flex;align-items:center;gap:14px;margin-bottom:12px">
            <div class="athkar-circle" data-id="${item.id}"
                style="width:72px;height:72px;border-radius:50%;
                  background:var(--ln-bg-attention,var(--ln-bg-card));
                  border:2.5px solid var(--ln-accent-ceremonial,var(--ln-accent));
                  display:flex;flex-direction:column;align-items:center;
                  justify-content:center;flex-shrink:0;
                  transition:transform var(--motion-duration-fast,.2s) var(--motion-easing-default,ease)">
              <span style="font-size:1.75rem;font-weight:700;line-height:1;
                  color:var(--ln-text-primary)">${count}</span>
              <span style="font-size:.7rem;color:var(--ln-text-secondary);margin-top:2px">
                ${t('yawmi_athkar_of', 'of')} ${item.target}
              </span>
            </div>
            <div style="flex:1">
              <div style="height:6px;background:var(--ln-border,rgba(0,0,0,.1));
                  border-radius:3px;overflow:hidden">
                <div style="height:6px;background:var(--ln-accent);border-radius:3px;
                    width:${pct}%;transition:width var(--motion-duration-slow,.3s) ease"></div>
              </div>
              ${done
                ? `<div style="font-size:.8rem;color:var(--ln-accent);
                      margin-top:4px;font-weight:600">
                    ✓ ${t('yawmi_athkar_complete', 'Complete')}
                  </div>`
                : ''}
            </div>
          </div>
          ${done ? '' : `
            <button class="athkar-tap" data-id="${item.id}"
                style="width:100%;min-height:64px;
                  border-radius:var(--ln-radius-lg);
                  background:var(--ln-accent);color:var(--ln-accent-text);
                  font-size:1rem;font-weight:700;border:none;cursor:pointer;
                  transition:transform var(--motion-duration-fast,.1s) var(--motion-easing-default,ease)">
              ${t('yawmi_athkar_tap_to_count', 'Tap to Count')}
            </button>
          `}
        </div>`;
    }).join('');

    this.el.innerHTML = `
      <div style="padding-bottom:8px">
        ${allDone
          ? `<div style="text-align:center;padding:12px 16px;
                background:var(--ln-bg-attention,rgba(0,0,0,.05));
                border-radius:var(--ln-radius-md);margin-bottom:14px;
                font-size:.9rem;color:var(--ln-accent);font-weight:600">
              ✓ ${t('yawmi_athkar_all_done', 'All adhkar complete for today')}
            </div>`
          : ''}
        ${cardsHtml}
        <div style="font-size:.7rem;color:var(--ln-text-secondary);
            text-align:center;margin-top:6px;font-style:italic;
            padding:0 4px">${this.sourceRef}</div>
      </div>`;

    // Tap to count
    this.el.querySelectorAll<HTMLButtonElement>('.athkar-tap').forEach(btn => {
      btn.addEventListener('pointerdown', () => { btn.style.transform = 'scale(0.96)'; });
      btn.addEventListener('pointerup', () => { btn.style.transform = ''; });
      btn.addEventListener('pointerleave', () => { btn.style.transform = ''; });
      btn.addEventListener('click', () => {
        const id = btn.dataset.id!;
        const item = this.items.find(i => i.id === id);
        if (!item) return;

        const prev = this.getCount(id);
        if (prev >= item.target) return; // already done
        const next = prev + 1;
        this.setCount(id, next);

        // Milestone animation + haptic
        if (MILESTONE_COUNTS.has(next) || next === item.target) {
          const circle = this.el.querySelector<HTMLElement>(`.athkar-circle[data-id="${id}"]`);
          if (circle) {
            circle.style.transform = 'scale(1.18)';
            setTimeout(() => { circle.style.transform = ''; }, 360);
          }
          try { navigator.vibrate?.([40, 10, 40]); } catch { /* ignore */ }

          // Mascot cue on full-target completion (per Motionov 3-surface design).
          // FoXplorr appears in Yawmi to celebrate the milestone — event-based
          // so MascotService can lazy-load + dismiss + suppress on Home Hero.
          if (next === item.target) {
            try {
              window.dispatchEvent(new CustomEvent('aweni:mascot:appear', {
                detail: { mascot: 'foxplorr', pose: 'eureka', surface: 'yawmi', reason: 'athkar_complete' },
              }));
            } catch { /* mascot service offline — silent */ }
          }
        }

        // Fire onComplete only on the transition from incomplete → complete
        const wasAllDone = this.items.every(i => i.id === id ? prev >= i.target : this.getCount(i.id) >= i.target);
        if (!wasAllDone && this.isAllDone()) this.onComplete?.();
        this.render(ctx);
      });
    });

    // Info buttons
    this.el.querySelectorAll<HTMLButtonElement>('.athkar-info').forEach(btn => {
      btn.addEventListener('click', () => {
        const id = btn.dataset.id!;
        const item = this.items.find(i => i.id === id);
        if (item) this.openInfoSheet(item, ctx);
      });
    });
  }
}
